import Mexp from "math-expression-evaluator"
import { useCallback, useEffect, useState, memo } from "react"
import { useFormContext } from '../forms/form'
import FieldError, { useFieldValidity } from './fieldError'

interface MathInputProps {
    value: string
    setValue: (value: string) => void
    isRequired?: boolean,
    negativeIsAllowed?: boolean,
    label: string
    helperText?: string
}

const MathInput: React.FC<MathInputProps> = ({ value, setValue, isRequired = false, negativeIsAllowed = true, label, helperText }) => {

    const [localValue, setLocalValue] = useState('0')
    const { disableInputs, isLoading } = useFormContext()
    const { errorMessage, isInvalid, fieldClassName, onBlur, onChange: onValidityChange, onInvalid } = useFieldValidity()

    const handleValueChange = useCallback((val: string) => {
        const mexp = new Mexp()
        let cleaned: string
        if (negativeIsAllowed) {
            // Remove all characters that aren't numbers, commas, periods. plus, multiplication, and minus.
            cleaned = val.replace(/[^0-9.,+*()/-]/g, ``)
            // Prevent multiple periods, plus, or multiplication in a row.
            cleaned = cleaned.replace(/([+*()-])\1+/g, `$1`)
        } else {
            // Remove all characters that aren't numbers, commas, periods. plus, multiplication, and minus.
            cleaned = val.replace(/[^0-9.,+*()/]/g, ``)
            // Prevent multiple periods, plus, or multiplication in a row.
            cleaned = cleaned.replace(/([+*()])\1+/g, `$1`)
        }
        // Replace commas with periods.
        cleaned = cleaned.replace(/[,]/g, `.`)
        // Prevent first character from being a period, plus, or multiplication.
        cleaned = cleaned.replace(/^([+*])/, ``)
        // Set local value.
        setLocalValue(cleaned)
        // Calculate the amount.
        let calculatedValue: string = ''
        try {
            calculatedValue = mexp.eval(cleaned).toString()
        }
        catch (e) {
            //console.error(e)
            return
        }
        // Set the value using provided setter.
        calculatedValue && setValue(calculatedValue)
    }, [negativeIsAllowed, setLocalValue, setValue])

    useEffect(() => {
        handleValueChange(localValue)
    }, [handleValueChange, localValue])

    return (
        <div className="input-holder">
            <label>{label}{isRequired ? '*' : ''}</label>
            <div className={`rounded-md border items-center focus-within:shadow-sm ${isInvalid ? 'border-[#FF4747]' : 'border-gray-300'}`}>
                <div className="flex flex-row">
                    <input
                        type={`text`}
                        value={localValue || ``}
                        onChange={(e) => {
                            onValidityChange(e)
                            setLocalValue(e.target.value)
                        }}
                        onBlur={onBlur}
                        onInvalid={onInvalid}
                        required={isRequired}
                        disabled={disableInputs}
                        className={`${fieldClassName} outline-none border-none focus:shadow-none rounded-none bg-transparent rounded-r-none ${isLoading ? 'animate-pulse' : ''}`}
                    />
                    <div className="bg-gray-300 text-base leading-8 px-4 whitespace-nowrap">= {value}</div>
                </div>
                <div className="bg-gray-200 text-base leading-10 px-4 whitespace-nowrap flex flex-row gap-x-2 justify-evenly">
                    {negativeIsAllowed && <div className={isLoading ? 'cursor-not-allowed' : 'cursor-pointer'} onClick={() => !disableInputs && setLocalValue(`${localValue}-`)}>-</div>}
                    <div className={isLoading ? 'cursor-not-allowed' : 'cursor-pointer'} onClick={() => !disableInputs && setLocalValue(`${localValue}+`)}>+</div>
                    <div className={isLoading ? 'cursor-not-allowed' : 'cursor-pointer'} onClick={() => !disableInputs && setLocalValue(`${localValue}*`)}>x</div>
                    <div className={isLoading ? 'cursor-not-allowed' : 'cursor-pointer'} onClick={() => !disableInputs && setLocalValue(`${localValue}/`)}>÷</div>
                    <div className={isLoading ? 'cursor-not-allowed' : 'cursor-pointer'} onClick={() => !disableInputs && setLocalValue(`${localValue}(`)}>(</div>
                    <div className={isLoading ? 'cursor-not-allowed' : 'cursor-pointer'} onClick={() => !disableInputs && setLocalValue(`${localValue})`)}>)</div>
                </div>
            </div>
            <FieldError message={errorMessage} />
            {helperText && (
                <div className="text-xs text-gray-500 mt-1">
                    {helperText}
                </div>
            )}
        </div>
    )
}

export default memo(MathInput)

